import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { examKey, levelKey, subjectKey } from 'modules/studyPlan/labels';
import type { GoalPlan } from 'modules/studyPlan';

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-border py-3">
      <h3 className="mb-1.5 text-[12.5px] font-semibold text-foreground/80">{title}</h3>
      <ul className="flex flex-col gap-1 text-[13px] text-foreground/80">{children}</ul>
    </div>
  );
}

interface HowBuiltCardProps {
  goals: GoalPlan[];
  dailyStudyMinutes: number;
  targetPercent: number;
}

/** Says where the plan's numbers come from, so nothing about it is a black box. */
export function HowBuiltCard({ goals, dailyStudyMinutes, targetPercent }: HowBuiltCardProps) {
  const { t } = useTranslation();
  const answers = goals.reduce((sum, goal) => sum + goal.mastery.answers, 0);
  const attempts = goals.reduce((sum, goal) => sum + goal.mastery.attempts, 0);
  const answersOf = (goal: GoalPlan) =>
    [goal.level ? t(levelKey(goal.level), { defaultValue: goal.level }) : '', ...goal.subjects.map((s) => t(subjectKey(s), { defaultValue: s }))]
      .filter(Boolean)
      .join(', ');

  return (
    <section aria-labelledby="plan-built" className="rounded-lg border border-border bg-card p-5">
      <h2 id="plan-built" className="text-[15px] font-semibold text-foreground">{t('studyPlan.built_title')}</h2>
      <div className="mt-2">
        <Group title={t('studyPlan.built_goal')}>
          {goals.map((goal) => {
            const exam = t(examKey(goal.exam), { defaultValue: goal.exam });
            const detail = answersOf(goal);
            return <li key={goal.exam}>{detail ? `${exam} · ${detail}` : exam}</li>;
          })}
          <li>{dailyStudyMinutes} {t('portal.minutes')} · {targetPercent}%</li>
        </Group>
        <Group title={t('studyPlan.built_activity')}>
          <li>
            {answers > 0 ? t('studyPlan.built_activity_count', { answers, attempts }) : t('studyPlan.built_activity_none')}
          </li>
        </Group>
        <Group title={t('studyPlan.built_rules')}>
          <li>{t('studyPlan.built_rule_days')}</li>
        </Group>
      </div>
      <p className="mt-1 flex items-start gap-2.5 rounded-md bg-muted p-3 text-[12.5px] leading-relaxed text-foreground/80">
        <ShieldCheck size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>{t('studyPlan.built_rule_ai')}</span>
      </p>
    </section>
  );
}

export default HowBuiltCard;
